import { useCallback, useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import DeckCard from "@/components/DeckBrowser/DeckCard"
import DeckChips from "@/components/DeckBrowser/DeckChips"
import { cardFace, cardIdsForFamily } from "@/content/cards"
import { DECK_BROWSER_COPY } from "@/content/cardsRound"
import type { Family } from "@/content/families"
import type { FamilyNumber } from "@/theme/colors"
import styles from "@/styles/components/DeckCardView.module.css"

/*
 * The deck browser's card view — one family at a time: tabs, a 3D card stack,
 * round Previous / Next buttons, and thumbnails. Ported from the Figma handoff.
 *
 * Browser-only. It renders `DeckCard`, never the in-session `CardFace`, so none
 * of this touches the pick exercise. The peel-on-hover and the stack live here;
 * `DeckBrowser` owns `family`/`index`, the page tint and the background wipe
 * (shared with the view toggle — see `pageWipe`), so a chip hands its click up.
 *
 * Previous / Next wrap WITHIN the family (four cards), matching the handoff; the
 * tabs move between families.
 */

const RM =
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches

const AL = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
)
const AR = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

interface DeckCardViewProps {
  inPlay: readonly Family[]
  family: FamilyNumber
  index: number
  /** Switch family — handed the clicked chip so the parent can anchor the wipe. */
  onFamilyPick: (n: FamilyNumber, el: HTMLElement) => void
  onIndex: (i: number) => void
  /** Open the current card — handed the card element so the overlay morphs from it. */
  onOpen: (el: HTMLElement) => void
  /** Card view is visible and nothing is layered over it: safe to own the keys. */
  active: boolean
  /** The view toggle, rendered on the right of the tabs row. */
  toggle?: ReactNode
}

export default function DeckCardView({
  inPlay,
  family,
  index,
  onFamilyPick,
  onIndex,
  onOpen,
  active,
  toggle,
}: DeckCardViewProps) {
  const cards = cardIdsForFamily(family)
  const n = cards.length
  const at = Math.min(index, Math.max(n - 1, 0))

  const tiltRef = useRef<HTMLDivElement>(null)
  const busyRef = useRef(false)

  const [lean, setLean] = useState<"l" | "r" | null>(null)
  const [anim, setAnim] = useState<"advance" | "pull" | null>(null)

  // The deck is keyed by family+index so each committed step mounts FRESH card
  // nodes — the handoff rebuilds the DOM every move, so the flung card is gone,
  // not transitioned back. Reusing the nodes is what caused the flick: removing
  // the `fling` class animated the top card back from off-screen.
  const topRef = useRef<HTMLButtonElement>(null)
  const mountedRef = useRef(false)

  // The `enter` settle plays only on a family switch (matching the handoff),
  // never on a step or the first mount. Driven here rather than via className so
  // a mid-settle re-render (a hover) can't tear the class off and cut it short.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    const el = topRef.current
    if (!el || RM) return
    el.classList.add(styles.enter)
    const timer = window.setTimeout(() => el.classList.remove(styles.enter), 600)
    return () => window.clearTimeout(timer)
  }, [family])

  const commit = useCallback(
    (i: number) => {
      onIndex(((i % n) + n) % n)
    },
    [onIndex, n]
  )

  const goNext = useCallback(() => {
    if (busyRef.current || n < 2) return
    setLean(null)
    if (RM) {
      commit(at + 1)
      return
    }
    busyRef.current = true
    setAnim("advance")
    // 480ms: a little past the 450ms transition, so the move finishes before the
    // fresh nodes mount (the handoff uses the same beat).
    window.setTimeout(() => {
      commit(at + 1)
      setAnim(null)
      busyRef.current = false
    }, 480)
  }, [at, n, commit])

  const goPrev = useCallback(() => {
    if (busyRef.current || n < 2) return
    setLean(null)
    if (RM) {
      commit(at - 1)
      return
    }
    busyRef.current = true
    setAnim("pull")
    window.setTimeout(() => {
      commit(at - 1)
      setAnim(null)
      busyRef.current = false
    }, 480)
  }, [at, n, commit])

  // ← / → navigate; Enter / Space opens the focused card.
  useEffect(() => {
    if (!active) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") goNext()
      else if (event.key === "ArrowLeft") goPrev()
      else if (
        (event.key === "Enter" || event.key === " ") &&
        (event.target as HTMLElement)?.dataset?.deckTop === "true"
      ) {
        event.preventDefault()
        if (topRef.current) onOpen(topRef.current)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [active, goNext, goPrev, onOpen])

  const onTilt = (event: React.MouseEvent) => {
    if (RM || !tiltRef.current) return
    const r = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const x = (event.clientX - r.left) / r.width - 0.5
    const y = (event.clientY - r.top) / r.height - 0.5
    tiltRef.current.style.transform = `rotateY(${x * 14}deg) rotateX(${-y * 12}deg) translateZ(10px)`
  }
  const resetTilt = () => {
    if (tiltRef.current) tiltRef.current.style.transform = ""
  }

  const prev = cards[(at - 1 + n) % n]
  const next = cards[(at + 1) % n]
  const nextNext = cards[(at + 2) % n]
  const current = cards[at]

  const deckClass = [
    styles.deck,
    lean === "l" && styles.leanL,
    lean === "r" && styles.leanR,
    anim === "advance" && styles.advance,
    anim === "pull" && styles.pull,
  ]
    .filter(Boolean)
    .join(" ")

  const topClass = [styles.card, styles.top, anim === "advance" && styles.fling]
    .filter(Boolean)
    .join(" ")

  return (
    <div className={styles.view}>
      <DeckChips
        families={inPlay}
        active={family}
        onPick={(n, el) => {
          // Don't switch families mid card-flight, or the stack and the wipe race.
          if (busyRef.current || n === family) return
          onFamilyPick(n as FamilyNumber, el)
        }}
        toggle={toggle}
      />

      <div className={lean ? `${styles.stage} ${styles.dim}` : styles.stage}>
        <div className={styles.left}>
          <button
            type="button"
            className={lean === "l" ? `${styles.rb} ${styles.rbHot}` : styles.rb}
            aria-label={DECK_BROWSER_COPY.previous}
            onClick={goPrev}
            onMouseEnter={() => !busyRef.current && setLean("l")}
            onMouseLeave={() => setLean(null)}
          >
            <span className={`${styles.rbCircle} ${styles.rbLight}`}>{AL}</span>
            <span className={styles.rbLabel}>Previous</span>
          </button>
        </div>

        <div className={deckClass} key={`${family}-${at}`}>
          <div className={`${styles.card} ${styles.pv}`}>
            <DeckCard cardId={prev} />
          </div>
          <div className={`${styles.card} ${styles.p2}`}>
            <DeckCard cardId={nextNext} />
          </div>
          <div className={`${styles.card} ${styles.p1}`}>
            <DeckCard cardId={next} />
          </div>
          <button
            type="button"
            data-deck-top="true"
            ref={topRef}
            className={topClass}
            aria-label={`Open ${cardFace(current)?.name ?? "card"}`}
            onClick={(event) => onOpen(event.currentTarget)}
            onMouseMove={onTilt}
            onMouseLeave={resetTilt}
          >
            <div className={styles.tilt} ref={tiltRef}>
              <DeckCard cardId={current} />
            </div>
          </button>
        </div>

        <div className={styles.right}>
          <button
            type="button"
            className={lean === "r" ? `${styles.rb} ${styles.rbHot}` : styles.rb}
            aria-label={DECK_BROWSER_COPY.next}
            onClick={goNext}
            onMouseEnter={() => !busyRef.current && setLean("r")}
            onMouseLeave={() => setLean(null)}
          >
            <span className={`${styles.rbCircle} ${styles.rbDark}`}>{AR}</span>
            <span className={styles.rbLabel}>Next</span>
          </button>
        </div>
      </div>

      <div className={styles.thumbs}>
        {cards.map((id, i) => (
          <button
            key={id}
            type="button"
            className={i === at ? `${styles.thumb} ${styles.thumbOn}` : styles.thumb}
            aria-current={i === at ? "true" : undefined}
            aria-label={cardFace(id)?.name ?? id}
            onClick={() => {
              // Clicking the selected thumbnail opens the card — morph from the
              // big stack card, not the 54px thumbnail (matches the handoff).
              if (i === at) {
                if (topRef.current) onOpen(topRef.current)
              } else {
                onIndex(i)
              }
            }}
          >
            <DeckCard cardId={id} />
          </button>
        ))}
      </div>
    </div>
  )
}
